import React, { useState, useEffect } from 'react';
import { AthleteProfile } from '../types';
import { Trophy, Zap, CheckCircle2, RotateCcw, Flame, Target, Sparkles, Award, ArrowRight, Share2, Users, Activity } from 'lucide-react';
import { triggerHaptic } from '../utils/haptics';
import { playSuccessChime } from '../utils/audio';
import { ShareGoalModal } from './ShareGoalModal';

export type ChallengeCategory = 'Skill' | 'Social' | 'Conditioning';

export interface WeeklyTask {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  rewardXp: number;
  icon: string;
  category: ChallengeCategory;
  sportLabel?: string;
  completed: boolean;
  claimed: boolean;
}

interface WeeklyChallengesProps {
  user: AthleteProfile;
  onEarnXp: (amount: number, source: string) => void;
  onOpenStatLog?: () => void;
}

const MOCK_TASK_COLLECTION: Omit<WeeklyTask, 'current' | 'completed' | 'claimed'>[] = [
  {
    id: 'wc_shots_10',
    title: 'Make 10 Shots in One Session',
    description: 'Sunk 10 field goals in a single stat-logging session.',
    target: 10,
    unit: 'Shots Made',
    rewardXp: 150,
    icon: '🎯',
    category: 'Skill',
    sportLabel: 'Basketball',
  },
  {
    id: 'wc_pts_30',
    title: 'Score 30 Total Points',
    description: 'Accumulate 30 total points across games logged this week.',
    target: 30,
    unit: 'PTS',
    rewardXp: 250,
    icon: '🔥',
    category: 'Skill',
    sportLabel: 'Scoring',
  },
  {
    id: 'wc_ast_5',
    title: 'Dish Out 5 Assists',
    description: 'Set up teammates with 5 key assist passes.',
    target: 5,
    unit: 'Assists',
    rewardXp: 180,
    icon: '🤝',
    category: 'Skill',
    sportLabel: 'Playmaking',
  },
  {
    id: 'wc_3pt_5',
    title: 'Sink 5 Three-Pointers',
    description: 'Drain 5 deep shots from beyond the arc.',
    target: 5,
    unit: '3PM',
    rewardXp: 200,
    icon: '⚡',
    category: 'Skill',
    sportLabel: 'Basketball',
  },
  {
    id: 'wc_courts_2',
    title: 'Check In at 2 Unique Courts',
    description: 'Explore the map and log games at 2 different playground courts.',
    target: 2,
    unit: 'Courts',
    rewardXp: 175,
    icon: '📍',
    category: 'Social',
    sportLabel: 'Court Map',
  },
  {
    id: 'wc_reel_1',
    title: 'Upload 1 Highlight Reel Video',
    description: 'Upload a 30-second game highlight clip to your profile feed.',
    target: 1,
    unit: 'Clip',
    rewardXp: 300,
    icon: '🎬',
    category: 'Social',
    sportLabel: 'Highlights',
  },
  {
    id: 'wc_teammate_1',
    title: 'Team Up with 1 New Player',
    description: 'Connect and play a pickup game with a newly added athlete friend.',
    target: 1,
    unit: 'Teammate',
    rewardXp: 160,
    icon: '👥',
    category: 'Social',
    sportLabel: 'Community',
  },
  {
    id: 'wc_games_2',
    title: 'Log 2 Games This Week',
    description: 'Record stats for 2 pickup or league games on any court.',
    target: 2,
    unit: 'Games',
    rewardXp: 200,
    icon: '🏀',
    category: 'Conditioning',
    sportLabel: 'All Sports',
  },
  {
    id: 'wc_reb_15',
    title: 'Grab 15 Rebounds',
    description: 'Control the glass by securing 15 offensive or defensive boards.',
    target: 15,
    unit: 'Rebounds',
    rewardXp: 220,
    icon: '📊',
    category: 'Conditioning',
    sportLabel: 'Defense',
  },
  {
    id: 'wc_streak_3',
    title: 'Maintain 3-Day Activity Streak',
    description: 'Log into Playground League 3 days in a row to keep your momentum going.',
    target: 3,
    unit: 'Days',
    rewardXp: 250,
    icon: '🏃',
    category: 'Conditioning',
    sportLabel: 'Endurance',
  },
];

export const WeeklyChallenges: React.FC<WeeklyChallengesProps> = ({ user, onEarnXp, onOpenStatLog }) => {
  const [tasks, setTasks] = useState<WeeklyTask[]>(() => {
    const saved = localStorage.getItem('playground_weekly_tasks_v3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure every task has category
        return parsed.map((t: any) => ({
          ...t,
          category: t.category || (t.id.includes('shots') || t.id.includes('pts') || t.id.includes('ast') || t.id.includes('3pt') ? 'Skill' : t.id.includes('courts') || t.id.includes('reel') || t.id.includes('team') ? 'Social' : 'Conditioning')
        }));
      } catch (e) {
        console.warn('Failed parsing saved tasks:', e);
      }
    }
    // Default initial selection covering Skill, Social, and Conditioning
    return [
      { ...MOCK_TASK_COLLECTION[0], current: 6, completed: false, claimed: false }, // Skill
      { ...MOCK_TASK_COLLECTION[4], current: 1, completed: false, claimed: false }, // Social
      { ...MOCK_TASK_COLLECTION[7], current: 2, completed: true, claimed: false },  // Conditioning
    ];
  });

  const [activeStatusFilter, setActiveStatusFilter] = useState<'all' | 'in_progress' | 'completed'>('all');
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<'all' | ChallengeCategory>('all');
  const [isShuffling, setIsShuffling] = useState<boolean>(false);
  const [sharingTask, setSharingTask] = useState<WeeklyTask | null>(null);

  // Save tasks state to localStorage
  useEffect(() => {
    localStorage.setItem('playground_weekly_tasks_v3', JSON.stringify(tasks));
  }, [tasks]);

  const handleSimulateProgress = (taskId: string) => {
    triggerHaptic('light');
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const newCurrent = Math.min(t.target, t.current + 1);
        const isFinished = newCurrent >= t.target;
        if (isFinished && !t.completed) {
          triggerHaptic('success');
          playSuccessChime();
        }
        return {
          ...t,
          current: newCurrent,
          completed: isFinished,
        };
      })
    );
  };

  const handleClaimReward = (task: WeeklyTask) => {
    if (task.claimed) return;
    triggerHaptic('success');
    
    // PLAY PLEASANT WEB AUDIO SUCCESS CHIME SOUND EFFECT!
    playSuccessChime();

    onEarnXp(task.rewardXp, `Weekly Challenge Completed: ${task.title}`);

    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, claimed: true, completed: true } : t))
    );
  };

  const handleRerollTasks = () => {
    triggerHaptic('medium');
    setIsShuffling(true);

    setTimeout(() => {
      // Pick 3 random tasks from collection (one from each category if possible)
      const skillTasks = MOCK_TASK_COLLECTION.filter((t) => t.category === 'Skill');
      const socialTasks = MOCK_TASK_COLLECTION.filter((t) => t.category === 'Social');
      const condTasks = MOCK_TASK_COLLECTION.filter((t) => t.category === 'Conditioning');

      const randSkill = skillTasks[Math.floor(Math.random() * skillTasks.length)];
      const randSocial = socialTasks[Math.floor(Math.random() * socialTasks.length)];
      const randCond = condTasks[Math.floor(Math.random() * condTasks.length)];

      const picked = [randSkill, randSocial, randCond].filter(Boolean);

      const newTasks: WeeklyTask[] = picked.map((t) => ({
        ...t,
        current: Math.floor(Math.random() * Math.max(1, t.target)),
        completed: false,
        claimed: false,
      }));
      setTasks(newTasks);
      setIsShuffling(false);
    }, 400);
  };

  const filteredTasks = tasks.filter((t) => {
    // Status filter
    if (activeStatusFilter === 'in_progress' && (t.claimed || t.current >= t.target)) return false;
    if (activeStatusFilter === 'completed' && (!t.claimed && t.current < t.target)) return false;

    // Category filter
    if (activeCategoryFilter !== 'all' && t.category !== activeCategoryFilter) return false;

    return true;
  });

  const completedCount = tasks.filter((t) => t.claimed || t.current >= t.target).length;

  const getCategoryBadgeColor = (cat: ChallengeCategory) => {
    switch (cat) {
      case 'Skill':
        return 'bg-amber-400/20 text-amber-300 border-amber-400/40';
      case 'Social':
        return 'bg-sky-400/20 text-sky-300 border-sky-400/40';
      case 'Conditioning':
        return 'bg-emerald-400/20 text-emerald-300 border-emerald-400/40';
      default:
        return 'bg-indigo-900 text-indigo-300 border-white/10';
    }
  };

  const getCategoryIcon = (cat: ChallengeCategory) => {
    switch (cat) {
      case 'Skill':
        return <Target className="w-3 h-3" />;
      case 'Social':
        return <Users className="w-3 h-3" />;
      case 'Conditioning':
        return <Activity className="w-3 h-3" />;
      default:
        return null;
    }
  };

  return (
    <div id="weekly-challenges-card" className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-6 md:p-8 rounded-[2.5rem] border border-lime-400/30 shadow-2xl space-y-6 relative overflow-hidden">
      {/* Background ambient glow */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/10 pb-5 relative z-10">
        <div className="flex items-center space-x-3.5">
          <div className="p-3 bg-gradient-to-tr from-lime-400 to-emerald-400 text-black rounded-2xl shadow-xl shadow-lime-400/20">
            <Trophy className="w-7 h-7 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
              <span className="px-3 py-0.5 bg-lime-400 text-black text-[10px] font-mono font-black rounded-full uppercase tracking-wider shadow">
                Weekly Active Tasks ⚡
              </span>
              <span className="text-xs text-indigo-300 font-mono font-bold">
                {completedCount} / {tasks.length} Completed
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-black italic uppercase text-white tracking-tight mt-1">
              Weekly Player Challenges & Rewards
            </h2>
          </div>
        </div>

        {/* Filter Controls Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category Toggle Filter */}
          <div className="flex bg-indigo-950/90 p-1 rounded-2xl border border-lime-400/30 text-xs font-mono shadow-inner">
            {(['all', 'Skill', 'Social', 'Conditioning'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  triggerHaptic('light');
                  setActiveCategoryFilter(cat);
                }}
                className={`px-3 py-1.5 rounded-xl font-black uppercase transition text-[10px] flex items-center space-x-1 ${
                  activeCategoryFilter === cat
                    ? 'bg-gradient-to-r from-lime-400 to-emerald-400 text-black shadow-md'
                    : 'text-indigo-300 hover:text-white'
                }`}
              >
                {cat !== 'all' && getCategoryIcon(cat as ChallengeCategory)}
                <span>{cat === 'all' ? 'All Categories' : cat}</span>
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="flex bg-indigo-950/80 p-1 rounded-2xl border border-white/10 text-xs font-mono">
            {(['all', 'in_progress', 'completed'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  triggerHaptic('light');
                  setActiveStatusFilter(filter);
                }}
                className={`px-2.5 py-1.5 rounded-xl font-bold transition uppercase text-[10px] ${
                  activeStatusFilter === filter
                    ? 'bg-indigo-800 text-white shadow'
                    : 'text-indigo-300 hover:text-white'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'in_progress' ? 'Active' : 'Done'}
              </button>
            ))}
          </div>

          {/* Reroll Button */}
          <button
            type="button"
            onClick={handleRerollTasks}
            disabled={isShuffling}
            className="px-3 py-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-white/15 rounded-2xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 shadow"
            title="Shuffle & load new weekly tasks"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${isShuffling ? 'animate-spin text-lime-400' : ''}`} />
            <span>Reroll</span>
          </button>
        </div>
      </div>

      {/* Task List Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
        {filteredTasks.length === 0 ? (
          <div className="col-span-full p-8 text-center bg-indigo-950/40 rounded-3xl border border-white/5 space-y-2">
            <Sparkles className="w-8 h-8 text-indigo-400 mx-auto" />
            <p className="text-sm font-bold text-white">No challenges match this category or status filter.</p>
            <button
              onClick={() => {
                setActiveCategoryFilter('all');
                setActiveStatusFilter('all');
              }}
              className="text-xs font-extrabold text-lime-400 hover:underline uppercase"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          filteredTasks.map((task) => {
            const isFinished = task.claimed || task.current >= task.target;
            const percent = Math.min(100, Math.round((task.current / task.target) * 100));

            return (
              <div
                key={task.id}
                className={`p-5 rounded-3xl border transition-all duration-300 flex flex-col justify-between space-y-4 relative overflow-hidden ${
                  task.claimed
                    ? 'bg-indigo-950/50 border-lime-400/30 opacity-80'
                    : isFinished
                    ? 'bg-gradient-to-b from-indigo-950 to-indigo-900 border-lime-400 ring-2 ring-lime-400/40 shadow-xl'
                    : 'bg-indigo-950/80 border-white/10 hover:border-lime-400/40'
                }`}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center space-x-2.5">
                      <span className="text-2xl p-2 bg-indigo-900/90 rounded-2xl border border-white/10 shadow-inner">
                        {task.icon}
                      </span>
                      <div>
                        <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                          <h3 className="font-extrabold text-sm text-white">{task.title}</h3>
                        </div>
                        <div className="flex items-center space-x-1.5 mt-0.5">
                          {/* Category Tag Badge */}
                          <span className={`px-2 py-0.5 rounded-md border text-[9px] font-mono font-black uppercase flex items-center space-x-1 ${getCategoryBadgeColor(task.category)}`}>
                            {getCategoryIcon(task.category)}
                            <span>{task.category}</span>
                          </span>

                          {task.sportLabel && (
                            <span className="text-[9px] font-mono text-indigo-300 uppercase block font-bold">
                              • {task.sportLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <span className="shrink-0 text-xs font-black italic text-amber-300 bg-amber-400/15 px-2.5 py-1 rounded-xl border border-amber-400/30 font-mono shadow">
                      +{task.rewardXp} XP
                    </span>
                  </div>

                  <p className="text-xs text-indigo-200/80 font-medium leading-relaxed">
                    {task.description}
                  </p>

                  {/* Progress Bar & Counter with Smooth Transition Animations */}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between items-center text-xs font-mono font-bold">
                      <span className="text-indigo-200/80 text-[11px]">Progress:</span>
                      <span className={isFinished ? 'text-lime-400 font-extrabold' : 'text-white'}>
                        {task.current} / {task.target} {task.unit} ({percent}%)
                      </span>
                    </div>

                    <div className="w-full bg-indigo-900 h-3 rounded-full overflow-hidden p-0.5 border border-white/10">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${
                          task.claimed || isFinished
                            ? 'bg-gradient-to-r from-lime-400 to-emerald-400 shadow-sm shadow-lime-400'
                            : 'bg-gradient-to-r from-amber-400 to-orange-500'
                        }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Actions, Claim Button & Share Goal Button */}
                <div className="pt-2 border-t border-white/10 flex flex-col space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    {task.claimed ? (
                      <div className="flex-1 py-2 bg-lime-400/20 text-lime-300 font-black italic text-xs rounded-xl border border-lime-400/40 flex items-center justify-center space-x-1.5">
                        <CheckCircle2 className="w-4 h-4 text-lime-400" />
                        <span>Reward Claimed (+{task.rewardXp} XP)</span>
                      </div>
                    ) : isFinished ? (
                      <button
                        type="button"
                        onClick={() => handleClaimReward(task)}
                        className="flex-1 py-2.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 hover:scale-105 transition flex items-center justify-center space-x-1.5 cursor-pointer"
                      >
                        <Award className="w-4 h-4 stroke-[2.5]" />
                        <span>Claim +{task.rewardXp} XP Reward 🔊</span>
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2 w-full justify-between">
                        <button
                          type="button"
                          onClick={() => handleSimulateProgress(task.id)}
                          className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-white/15 text-xs font-bold uppercase rounded-xl transition"
                        >
                          +1 Progress
                        </button>

                        {onOpenStatLog && (
                          <button
                            type="button"
                            onClick={() => {
                              triggerHaptic('light');
                              onOpenStatLog();
                            }}
                            className="px-3 py-1.5 bg-lime-400/10 hover:bg-lime-400/20 text-lime-300 border border-lime-400/30 text-xs font-extrabold uppercase rounded-xl transition flex items-center space-x-1"
                          >
                            <span>Log Stats</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* SHARE GOAL BUTTON */}
                    <button
                      type="button"
                      onClick={() => {
                        triggerHaptic('light');
                        setSharingTask(task);
                      }}
                      className="px-3 py-2 bg-indigo-900/90 hover:bg-indigo-800 text-lime-300 border border-lime-400/30 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1 shrink-0 cursor-pointer shadow"
                      title="Share goal social media preview card"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      <span>Share Goal</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* SHARE GOAL MODAL */}
      <ShareGoalModal
        isOpen={sharingTask !== null}
        onClose={() => setSharingTask(null)}
        task={sharingTask}
        user={user}
      />
    </div>
  );
};
